/**
 * Pacote de texto ainda aberto (o anel está contando) fica PRESO NO FIM da
 * conversa — decisão do dono, 20/set/2026.
 *
 * A bolha nasce com o carimbo do 1º texto, mas a mensagem só existe pro cliente
 * quando o pacote fecha. Se ele manda algo enquanto o relógio corre, no
 * WhatsApp dele (e no celular espelhado) a mensagem dele vem ANTES da nossa.
 * Ordenar só pelo carimbo colocava a dele abaixo. Aqui a lista continua
 * ordenada pelo carimbo e, no fim, os pacotes abertos — na ordem em que
 * estavam.
 *
 * ⚠️ Quem decide "aberto" é o RELÓGIO DO SERVIDOR, não a presença do prazo.
 * O prazo (`batch_open_until`) é hora do servidor e é, por definição, o
 * instante em que o pacote fecha (timer + resgate garantem). O aviso "pacote
 * fechou" pode não chegar (aba fora da sala — caso real do Ricardo, 21/set:
 * a bolha ficava presa até o F5). Então:
 *   - antes do prazo → presa no fim;
 *   - a partir do prazo → ordenada COMO SE o carimbo fosse o próprio prazo,
 *     que é o que o banco vai ter (fechou = prazo + milissegundos);
 *   - quando o aviso chega, ele só confirma o carimbo exato.
 * Sem folga e sem chute: com ou sem aviso, a tela fica igual ao banco.
 *
 * Puro e genérico (qualquer item com `direction`/`batch_open_until`), pra
 * testar sem React.
 */

interface ItemDaConversa {
  direction: string;
  created_at: string;
  /** string = prazo (aberto); null/ausente = fechado. */
  batch_open_until?: string | null | undefined;
}

/** Prazo do pacote em ms, ou null se não é um pacote (ou o prazo é inválido). */
function prazoMs(m: ItemDaConversa): number | null {
  if (m.direction !== 'outbound' || typeof m.batch_open_until !== 'string') return null;
  const prazo = Date.parse(m.batch_open_until);
  return Number.isNaN(prazo) ? null : prazo;
}

export function pacoteAberto(m: ItemDaConversa, agoraMs: number): boolean {
  const prazo = prazoMs(m);
  return prazo !== null && prazo > agoraMs;
}

/**
 * Carimbo pelo qual o item é ORDENADO. Pacote cujo prazo já venceu (e cujo
 * aviso ainda não chegou) vale o prazo — nunca menos que o carimbo próprio.
 * Devolve ISO pra comparar com os demais itens (todos ISO).
 */
export function carimboDeOrdenacao(m: ItemDaConversa, agoraMs: number): string {
  const prazo = prazoMs(m);
  if (prazo === null || prazo > agoraMs) return m.created_at;
  const prazoIso = new Date(prazo).toISOString();
  return prazoIso > m.created_at ? prazoIso : m.created_at;
}

/**
 * Recebe a lista já ordenada e devolve a mesma lista com os pacotes abertos
 * (pelo relógio `agoraMs`) movidos pro fim, ordem relativa preservada. Sem
 * pacote aberto, devolve a própria lista.
 */
export function comPacotesAbertosNoFim<T>(
  itens: readonly T[],
  mensagem: (t: T) => ItemDaConversa | null,
  agoraMs: number,
): T[] {
  const abertos: T[] = [];
  const resto: T[] = [];
  for (const it of itens) {
    const m = mensagem(it);
    (m && pacoteAberto(m, agoraMs) ? abertos : resto).push(it);
  }
  return abertos.length === 0 ? [...itens] : [...resto, ...abertos];
}

/**
 * Próximo instante em que algum item preso solta (= menor prazo ainda no
 * futuro), pra quem renderiza agendar o re-render; null se nada está preso.
 */
export function proximoDesprendimento<T>(
  itens: readonly T[],
  mensagem: (t: T) => ItemDaConversa | null,
  agoraMs: number,
): number | null {
  let proximo: number | null = null;
  for (const it of itens) {
    const m = mensagem(it);
    if (!m) continue;
    const prazo = prazoMs(m);
    if (prazo === null || prazo <= agoraMs) continue;
    if (proximo === null || prazo < proximo) proximo = prazo;
  }
  return proximo;
}
